import { Injectable } from '@angular/core';
import { Rgba, Hsla, Hsva } from './classes';

@Injectable()
export class ColorPickerService {
    constructor() { }

    hsla2hsva(hsla: Hsla): Hsva {
        const h = Math.min(hsla.h, 1),
            s = Math.min(hsla.s, 1),
            l = Math.min(hsla.l, 1),
            a = Math.min(hsla.a, 1);
        if (l === 0) {
            return new Hsva(h, 0, 0, a);
        } else {
            const v = l + s * (1 - Math.abs(2 * l - 1)) / 2;
            return new Hsva(h, 2 * (v - l) / v, v, a);
        }
    }

    rgbaToHsva(rgba: Rgba): Hsva {
        const r = Math.min(rgba.r, 1),
            g = Math.min(rgba.g, 1),
            b = Math.min(rgba.b, 1),
            a = Math.min(rgba.a, 1);
        const max = Math.max(r, g, b),
            min = Math.min(r, g, b);
        const v = max,
            d = max - min;
        let h: number, s: number;

        s = max === 0 ? 0 : d / max;
        if (max === min) {
            h = 0;
        } else {
            switch (max) {
                case r:
                    h = (g - b) / d + (g < b ? 6 : 0);
                    break;
                case g:
                    h = (b - r) / d + 2;
                    break;
                case b:
                    h = (r - g) / d + 4;
                    break;
            }
            h /= 6;
        }
        return new Hsva(h, s, v, a);
    }

    hsvaToRgba(hsva: Hsva): Rgba {
        const h = hsva.h,
            s = hsva.s,
            v = hsva.v,
            a = hsva.a;
        const i = Math.floor(h * 6),
            f = h * 6 - i,
            p = v * (1 - s),
            q = v * (1 - f * s),
            t = v * (1 - (1 - f) * s);
        let r: number, g: number, b: number;

        switch (i % 6) {
            case 0:
                r = v;
                g = t;
                b = p;
                break;
            case 1:
                r = q;
                g = v;
                b = p;
                break;
            case 2:
                r = p;
                g = v;
                b = t;
                break;
            case 3:
                r = p;
                g = q;
                b = v;
                break;
            case 4:
                r = t;
                g = p;
                b = v;
                break;
            case 5:
                r = v;
                g = p;
                b = q;
                break;
        }
        return new Rgba(r, g, b, a);
    }

    stringToHsva(colorString: string = ''): Hsva {
        const stringParsers = [{
            re: /#([a-fA-F0-9]{2})([a-fA-F0-9]{2})([a-fA-F0-9]{2})$/,
            parse: function (execResult: any) {
                return new Rgba(parseInt(execResult[1], 16) / 255,
                    parseInt(execResult[2], 16) / 255,
                    parseInt(execResult[3], 16) / 255,
                    1);
            }
        }];
        colorString = colorString.toLowerCase();
        let hsva: Hsva = null;
        for (const key in stringParsers) {
            if (stringParsers.hasOwnProperty(key)) {
                const parser = stringParsers[key];
                const match = parser.re.exec(colorString),
                    color: any = match && parser.parse(match);
                if (color) {
                    if (color instanceof Rgba) {
                        hsva = this.rgbaToHsva(color);
                    } else if (color instanceof Hsla) {
                        hsva = this.hsla2hsva(color);
                    }
                    return hsva;
                }
            }
        }
        return hsva;
    }

    outputFormat(hsva: Hsva): string {
        return this.hexText(this.denormalizeRGBA(this.hsvaToRgba(hsva)));
    }

    hexText(rgba: Rgba): string {
        return '#' + ((1 << 24) | (rgba.r << 16) | (rgba.g << 8) | rgba.b).toString(16).substr(1);
    }

    denormalizeRGBA(rgba: Rgba): Rgba {
        return new Rgba(Math.round(rgba.r * 255), Math.round(rgba.g * 255), Math.round(rgba.b * 255), rgba.a);
    }

}



// WEBPACK FOOTER //
// ./src/app/components/color-picker/color-picker.service.ts